import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { JobRequestSummaryDto } from '@models/job-request-summary.interface';

@Component({
  selector: 'app-job-request-tile',
  standalone: true,
  imports: [CommonModule, DatePipe, MatIconModule],
  templateUrl: './job-request-tile.component.html',
  styleUrls: ['./job-request-tile.component.scss']
})
export class JobRequestTileComponent {
  @Input() jobRequest: JobRequestSummaryDto | null = null;
  @Input() isEditable = false;
  @Input() canManageJobRequests = true;

  @Output() goToDetails = new EventEmitter<JobRequestSummaryDto>();
  @Output() edit = new EventEmitter<JobRequestSummaryDto>();
  @Output() delete = new EventEmitter<JobRequestSummaryDto>();
  @Output() ship = new EventEmitter<JobRequestSummaryDto>();
  @Output() sendReport = new EventEmitter<{ jobRequest: JobRequestSummaryDto; isReportSent: boolean }>();
  @Output() openReport = new EventEmitter<JobRequestSummaryDto>();
  @Output() openColumnReport = new EventEmitter<JobRequestSummaryDto>();
  @Output() openWorksheet = new EventEmitter<JobRequestSummaryDto>();
  @Output() lock = new EventEmitter<JobRequestSummaryDto>();
  @Output() unlock = new EventEmitter<JobRequestSummaryDto>();

  protected onView(): void { if (this.jobRequest) this.goToDetails.emit(this.jobRequest); }
  protected onEdit(): void { if (this.jobRequest) this.edit.emit(this.jobRequest); }
  protected onDelete(): void { if (this.jobRequest) this.delete.emit(this.jobRequest); }
  protected onOpenReport(): void { if (this.jobRequest) this.openReport.emit(this.jobRequest); }
  protected onOpenColumnReport(): void { if (this.jobRequest) this.openColumnReport.emit(this.jobRequest); }
  protected onOpenWorksheet(): void { if (this.jobRequest) this.openWorksheet.emit(this.jobRequest); }
  protected onLock(): void { if (this.jobRequest) this.lock.emit(this.jobRequest); }
  protected onUnlock(): void { if (this.jobRequest) this.unlock.emit(this.jobRequest); }

  protected onShipChange(event: Event): void {
    if (!this.jobRequest || !this.isEditable) return;
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.ship.emit(this.jobRequest);
    }
    // Unchecking is not supported in simplified flow; reset visual state
    (event.target as HTMLInputElement).checked = this.jobRequest.hasShipmentRecords;
  }

  protected onReportSentChange(event: Event): void {
    if (!this.jobRequest || !this.isEditable) return;
    const checked = (event.target as HTMLInputElement).checked;
    this.sendReport.emit({ jobRequest: this.jobRequest, isReportSent: checked });
  }

  protected statusClass(status?: string | null): string {
    if (!status) return 'status-unknown';
    const s = status.toLowerCase();
    if (s.includes('pass') || s.includes('ok') || s.includes('complete') || s.includes('completed')) return 'status-pass';
    if (s.includes('fail') || s.includes('failed') || s.includes('error')) return 'status-fail';
    if (s.includes('pending') || s.includes('in progress') || s.includes('inprogress')) return 'status-pending';
    return 'status-unknown';
  }

  protected statusLabel(status?: string | null): string {
    return status ?? 'Unknown';
  }

  protected get isCutback(): boolean {
    return this.jobRequest?.isDensityChange === true
      && this.jobRequest.densityChangeClassification === 1;
  }

  protected get isCutbackCompleted(): boolean {
    const status = this.jobRequest?.statusName?.trim().toLowerCase();
    return status === 'passed' || status === 'complete' || status === 'completed' || status === 'locked';
  }

  protected get shipmentText(): string {
    const jr = this.jobRequest;
    if (jr?.hasShipmentRecords) return jr.shipmentRecordsText ?? 'Shipped';
    return 'Not Shipped';
  }

  protected get isPilotTest(): boolean {
    return (this.jobRequest?.jobRequestTypeName ?? '').trim().toLowerCase() === 'pilottest';
  }
}
